import React, { useEffect } from "react";
import "./App.css";
import "./i18n/i18n";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import InstallBanner from "./components/InstallBanner";
import { useBackendWakeUp } from "./hooks/useBackendWakeUp";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Machines from "./pages/Machines";
import Accessories from "./pages/Accessories";
import ProductDetail from "./pages/ProductDetail";
import AccessoryDetail from "./pages/AccessoryDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import PayPalSuccess from "./pages/PayPalSuccess";
import PayPalCancel from "./pages/PayPalCancel";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Preorder from "./pages/Preorder";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import DatenschutzSocialMedia from "./pages/DatenschutzSocialMedia";
import Cookies from "./pages/Cookies";
import CookieRichtlinie from "./pages/CookieRichtlinie";
import Garantiebedingungen from "./pages/Garantiebedingungen";
import AGB from "./pages/AGB";
import Widerrufsbelehrung from "./pages/Widerrufsbelehrung";
import FAQ from "./pages/FAQ";
import Gewaehrleistung from "./pages/Gewaehrleistung";
import Zahlungsarten from "./pages/Zahlungsarten";
import PartnerWerden from "./pages/PartnerWerden";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import MeinKonto from "./pages/MeinKonto";
import AdminDashboard from "./pages/AdminDashboard";
// PWA Components
import PWAPreview, { PWAProductPreview } from "./components/pwa/PWAPreview";
import PWAApp from "./components/pwa/PWAApp";
import { isPWAMode } from "./pwa";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Check if running as installed PWA
function useIsPWA() {
  const [isPWA, setIsPWA] = React.useState(false);
  
  useEffect(() => {
    // Check standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = window.navigator.standalone === true;
    
    // Check URL parameter for testing (?pwa=true)
    const urlParams = new URLSearchParams(window.location.search);
    const testMode = urlParams.get('pwa') === 'true';
    
    // Check if opened from PWA start_url
    const isPWASource = urlParams.get('source') === 'pwa';
    
    setIsPWA(isStandalone || isIOSStandalone || testMode || isPWASource);
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = (e) => setIsPWA(e.matches);
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);
  
  return isPWA;
}

// Layout component that conditionally renders Header/Footer
function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/admin');
  const isPWAPreview = pathname.startsWith('/pwa-preview');

  // PWA Preview routes get their own layout
  if (isPWAPreview) {
    return <>{children}</>;
  }

  return (
    <>
      {!isAdminRoute && <Header />}
      <main>{children}</main>
      {!isAdminRoute && <Footer />}
    </>
  );
}

// Redirect component for old /maschinen/:slug URLs to new category-based URLs
function LegacyProductRedirect() {
  const { slug } = useParams();
  
  // Map old slugs to new slugs (all use QW now)
  const slugRedirectMap = {
    'mini-bagger-qw10': 'mini-bagger-qw10',
    'mini-bagger-qw12': 'mini-bagger-qw12',
    'mini-bagger-qw12s': 'mini-bagger-qw12s',
    'mini-bagger-qw13': 'mini-bagger-qw13',
    'mini-bagger-qw15': 'mini-bagger-qw15',
    'mini-bagger-qw16': 'mini-bagger-qw15',
    'mini-bagger-qw18': 'mini-bagger-qw18',
    'mini-bagger-qw20': 'mini-bagger-qw20',
    'mini-bagger-qw22': 'mini-bagger-qw22',
    // Old QV slugs redirect to QW
    'mini-bagger-qv10': 'mini-bagger-qw10',
    'mini-bagger-qv12': 'mini-bagger-qw12',
    'mini-bagger-qv12s': 'mini-bagger-qw12s',
    'mini-bagger-qv13': 'mini-bagger-qw13',
    'mini-bagger-qv15': 'mini-bagger-qw15',
    'mini-bagger-qv18': 'mini-bagger-qw18',
    'mini-bagger-qv20': 'mini-bagger-qw20',
    'mini-bagger-qv22': 'mini-bagger-qw22',
    // Old QW25/QW28 redirects to similar models
    'mini-bagger-qw25': 'mini-bagger-qw12s',
    'mini-bagger-qw28': 'mini-bagger-qw13',
  };
  
  // Map slugs to their categories for 301-style redirect
  const slugToCategoryMap = {
    // Bagger (Mini-Bagger) - QW Bezeichnungen
    'mini-bagger-qw10': 'bagger',
    'mini-bagger-qw12': 'bagger',
    'mini-bagger-qw12s': 'bagger',
    'mini-bagger-qw13': 'bagger',
    'mini-bagger-qw15': 'bagger',
    'mini-bagger-qw18': 'bagger',
    'mini-bagger-qw20': 'bagger',
    'mini-bagger-qw22': 'bagger',
    // Radlader
    'radlader-zl08': 'radlader',
    'radlader-zl08-2': 'radlader',
    'radlader': 'radlader',
    // Gabelstapler
    'gabelstapler-cpd20k': 'gabelstapler',
    'gabelstapler-cpd30k': 'gabelstapler',
    // Rüttelplatten
    'ruettelplatte-pc90': 'ruettelplatten',
    'ruettelplatte-pc160': 'ruettelplatten',
    'ruettelplatte-stp90': 'ruettelplatten',
    'ruettelplatte-stp270': 'ruettelplatten',
    'ruettelplatte': 'ruettelplatten',
    // Stampfer
    'stampfer-bmr80': 'stampfer',
    'stampfer-bmr80-loncin': 'stampfer',
  };
  
  // First, redirect old QW slugs to new QV slugs
  const newSlug = slugRedirectMap[slug] || slug;
  const category = slugToCategoryMap[newSlug] || 'bagger';
  return <Navigate to={`/${category}/${newSlug}`} replace />;
}

function App() {
  const isPWA = useIsPWA();
  
  // CRITICAL: Wake up the backend container as soon as the page loads.
  // Kubernetes scales the backend to zero during inactivity, which causes
  // image requests (/api/uploads/...) to fail with 502 on first visit.
  useBackendWakeUp();
  
  // If running as PWA, show the app-style UI
  if (isPWA) {
    return (
      <div className="App pwa-mode">
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <CartProvider>
              <PWAApp />
              <Toaster position="top-center" richColors />
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    );
  }
  
  // Normal website mode
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <CartProvider>
            <Layout>
              <Routes>
                {/* PWA Preview Routes - for testing app design */}
                <Route path="/pwa-preview" element={<PWAPreview />} />
                <Route path="/pwa-preview/bagger" element={<PWAProductPreview />} />
                
                <Route path="/" element={<Home />} />
                <Route path="/leistungen" element={<Services />} />
                
                {/* New category-based product routes */}
                <Route path="/bagger" element={<Machines />} />
                <Route path="/bagger/:slug" element={<ProductDetail />} />
                <Route path="/radlader" element={<Machines />} />
                <Route path="/radlader/:slug" element={<ProductDetail />} />
                <Route path="/gabelstapler" element={<Machines />} />
                <Route path="/gabelstapler/:slug" element={<ProductDetail />} />
                <Route path="/ruettelplatten" element={<Machines />} />
                <Route path="/ruettelplatten/:slug" element={<ProductDetail />} />
                <Route path="/stampfer" element={<Machines />} />
                <Route path="/stampfer/:slug" element={<ProductDetail />} />
                
                {/* Legacy redirects for SEO - old /maschinen URLs */}
                <Route path="/maschinen" element={<Navigate to="/bagger" replace />} />
                <Route path="/maschinen/:slug" element={<LegacyProductRedirect />} />
                
                <Route path="/zubehoer" element={<Accessories />} />
                <Route path="/warenkorb" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/paypal-success" element={<PayPalSuccess />} />
                <Route path="/checkout/paypal-cancel" element={<PayPalCancel />} />
                <Route path="/zubehoer/:slug" element={<AccessoryDetail />} />
                <Route path="/vorbestellen" element={<Preorder />} />
                <Route path="/ueber-uns" element={<About />} />
                <Route path="/kontakt" element={<Contact />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/datenschutz-social-media" element={<DatenschutzSocialMedia />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/cookie-richtlinie" element={<CookieRichtlinie />} />
                <Route path="/garantiebedingungen" element={<Garantiebedingungen />} />
                <Route path="/agb" element={<AGB />} />
                <Route path="/widerrufsbelehrung" element={<Widerrufsbelehrung />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/gewaehrleistung" element={<Gewaehrleistung />} />
                <Route path="/zahlungsarten" element={<Zahlungsarten />} />
                <Route path="/partner-werden" element={<PartnerWerden />} />
                <Route path="/anmelden" element={<Login />} />
                <Route path="/registrieren" element={<Register />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/mein-konto" element={<MeinKonto />} />
                <Route path="/admin" element={<AdminDashboard />} />
              </Routes>
            </Layout>
            <ChatWidget />
            <InstallBanner />
            <Toaster />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
